import { useEffect, useState } from "react";
import { X, Barcode, Sparkles, ShoppingBag, AlertTriangle } from "lucide-react";

import { api, formatCurrency, formatDate } from "@/lib/api";

/**
 * Serial-number capture dialog for pending_fulfillment receipts.
 * Posts to POST /api/receipts/{id}/fulfill which wraps the existing mint flow.
 */
export default function FulfillDialog({ open, receipt, onClose, onFulfilled }) {
  const [serial, setSerial] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setSerial("");
      setError("");
      setSubmitting(false);
    }
  }, [open, receipt?.id]);

  if (!open || !receipt) return null;

  const item = receipt.items?.[0];
  const unitIndex = receipt.source_unit_index;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = serial.trim();
    if (!value) {
      setError("Serial number is required");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/receipts/${receipt.id}/fulfill`, {
        serial_number: value,
      });
      onFulfilled?.(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Fulfillment failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="fulfill-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <Barcode size={16} />
            </span>
            <div>
              <div className="font-display font-semibold text-gray-900">
                Fulfill Item
              </div>
              <div className="text-[11px] tracking-wider uppercase text-gray-500">
                Assign serial to mint certificate
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="fulfill-close"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
            <div className="flex items-start gap-3">
              <ShoppingBag size={16} className="text-gray-400 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 truncate">
                  {item?.name || "Item"}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                  <span className="font-mono">{receipt.receipt_id}</span>
                  {receipt.source_order_id && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span>Order {receipt.source_order_id}</span>
                    </>
                  )}
                  {unitIndex && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span>Unit {unitIndex}</span>
                    </>
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {formatCurrency(receipt.total, receipt.currency)} ·{" "}
                  {formatDate(receipt.purchase_date)}
                </div>
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="fulfill-serial"
              className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 mb-1.5"
            >
              Serial Number
            </label>
            <input
              id="fulfill-serial"
              data-testid="fulfill-serial-input"
              type="text"
              autoFocus
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              placeholder="e.g. SN-OMEGA-9400-A"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
            />
            <div className="mt-1.5 text-[11px] text-gray-500">
              On submit, Authera will mint the certificate on-chain and email the
              customer.
            </div>
          </div>

          {error && (
            <div
              data-testid="fulfill-error"
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/60 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="fulfill-cancel"
            className="text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="fulfill-submit"
            className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-sm font-semibold px-4 py-2 rounded-md shadow-sm hover:bg-[#0044CC] transition-all disabled:opacity-60 disabled:cursor-wait"
          >
            <Sparkles size={14} />
            {submitting ? "Issuing…" : "Fulfill & Issue"}
          </button>
        </div>
      </form>
    </div>
  );
}
